import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaPhone, FaWhatsapp } from 'react-icons/fa';
import { API_URL } from '../../config';

const FormContainer = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Title = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}33;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #f9f9f9;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #f0f0f0;
  }

  input[type="checkbox"] {
    width: auto;
    margin: 0;
    cursor: pointer;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  background: ${props => props.theme.colors.primary};
  color: white;

  &:hover {
    background: ${props => props.theme.colors.primary}dd;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: #00C851;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const BranchForm = ({ onSubmit, initialData = null }) => {
  const [branch, setBranch] = useState({
    name: '',
    location: '',
    phone: '',
    hasWhatsApp: false,
    whatsappNumber: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setBranch({
        name: initialData.name || '',
        location: initialData.location || '',
        phone: initialData.phone || '',
        hasWhatsApp: initialData.hasWhatsApp || false,
        whatsappNumber: initialData.whatsappNumber || ''
      });
    }
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const isEdit = !!initialData;
      const url = isEdit 
        ? `${API_URL}/home-content/branches/${initialData._id}`
        : `${API_URL}/home-content/branches`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(branch)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} branch`);
      }

      const data = await response.json();
      setSuccess(`Branch ${isEdit ? 'updated' : 'created'} successfully!`);
      
      if (!isEdit) {
        // Only reset form for new branches
        setBranch({ name: '', location: '', phone: '', hasWhatsApp: false, whatsappNumber: '' });
      }
      
      if (onSubmit) onSubmit(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setBranch(prev => ({ ...prev, [field]: value }));
  };

  return (
    <FormContainer
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Title>{initialData ? 'Edit Branch' : 'Add New Branch'}</Title>
      <Form onSubmit={handleSubmit}>
        <InputGroup>
          <Label>Branch Name</Label>
          <Input
            type="text"
            value={branch.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Embassy of Life Chapel – Headquarters"
            required
          />
        </InputGroup>

        <InputGroup>
          <Label>Location</Label>
          <Input
            type="text"
            value={branch.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="e.g., South Patasi - Kumasi"
            required
          />
        </InputGroup>

        <InputGroup>
          <Label>
            <FaPhone /> Phone Number
          </Label>
          <Input
            type="tel"
            value={branch.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="e.g., +233540122671"
          />
        </InputGroup>

        <InputGroup>
          <CheckboxGroup onClick={() => handleChange('hasWhatsApp', !branch.hasWhatsApp)}>
            <input
              type="checkbox"
              checked={branch.hasWhatsApp}
              onChange={(e) => handleChange('hasWhatsApp', e.target.checked)}
            />
            <Label style={{ margin: 0, cursor: 'pointer' }}>
              <FaWhatsapp /> Enable WhatsApp for this branch
            </Label>
          </CheckboxGroup>
        </InputGroup>

        {branch.hasWhatsApp && (
          <InputGroup>
            <Label>
              <FaWhatsapp /> WhatsApp Number
            </Label>
              <Input
                type="tel"
                value={branch.whatsappNumber}
                onChange={(e) => handleChange('whatsappNumber', e.target.value)}
                placeholder="e.g., +233540122670"
              />
          </InputGroup>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : (initialData ? 'Update Branch' : 'Create Branch')}
        </Button>
      </Form>
    </FormContainer>
  );
};

export default BranchForm; 